import { Box, Chip, LinearProgress, Paper, Skeleton, Tooltip, Typography } from '@mui/material'
import { FC, ReactNode } from 'react'
import { tokens } from '@/theme'
import { formatCNPJ } from '@/utils/formatters'
import { ETAPA_COMERCIAL_LABEL, STATUS_LABEL, etapaComercialChipColor } from '@/types/statusReserva'
import { ChecklistReserva, EstadoChecklist, ReservaEstruturacao } from '@/types/estruturacao'

interface Props {
  reserva: ReservaEstruturacao
  /** Nome do produto do catálogo; nulo cai no `tipoNecessidade` da reserva. */
  produtoNome?: string | null
  /** Checklist da reserva — fonte do "Documentação %" (válido quando `checklistEstado === 'ok'`). */
  checklist: ChecklistReserva | null
  /** carregando → esqueleto · ok → percentual · erro → "—" (nunca esqueleto eterno). */
  checklistEstado: EstadoChecklist
}

const Bloco: FC<{ rotulo: string; children: ReactNode }> = ({ rotulo, children }) => (
  <Box sx={{ minWidth: 160 }}>
    <Typography
      variant="caption"
      component="div"
      sx={{
        textTransform: 'uppercase',
        fontWeight: 600,
        letterSpacing: '0.06em',
        color: tokens.textSecondary,
        mb: 0.5,
      }}
    >
      {rotulo}
    </Typography>
    {children}
  </Box>
)

/**
 * Header-resumo do workspace de estruturação (Figma 12:*): Oportunidade · Produto ·
 * Status (chip da EtapaComercial; o status operacional de 18 estados fica no tooltip) ·
 * Documentação ("68% · 8 de 12", do percentual do checklist — mesma fonte do backend).
 */
export const HeaderResumoEstruturacao: FC<Props> = ({
  reserva,
  produtoNome,
  checklist,
  checklistEstado,
}) => {
  const empresa = reserva.razaoSocial ?? reserva.nomeFantasia ?? reserva.nomeCliente ?? '—'
  const statusOperacional = reserva.status ? STATUS_LABEL[reserva.status] : 'Não informado'

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        mb: 3,
        display: 'flex',
        flexWrap: 'wrap',
        gap: 4,
        alignItems: 'flex-start',
        border: `1px solid ${tokens.divider}`,
      }}
    >
      <Bloco rotulo="Oportunidade">
        <Typography sx={{ fontWeight: 700, color: tokens.brandInk }}>{empresa}</Typography>
        <Typography variant="caption" sx={{ color: tokens.textSecondary }}>
          {reserva.cnpj ? formatCNPJ(reserva.cnpj) : 'CNPJ não informado'}
        </Typography>
      </Bloco>

      <Bloco rotulo="Produto">
        <Typography sx={{ fontWeight: 600, color: tokens.brandInk }}>
          {produtoNome ?? reserva.tipoNecessidade ?? '—'}
        </Typography>
      </Bloco>

      <Bloco rotulo="Status">
        <Tooltip title={`Status operacional: ${statusOperacional}`}>
          {reserva.etapaComercial ? (
            <Chip
              size="small"
              label={ETAPA_COMERCIAL_LABEL[reserva.etapaComercial]}
              color={etapaComercialChipColor(reserva.etapaComercial)}
            />
          ) : (
            <Chip size="small" label={statusOperacional} />
          )}
        </Tooltip>
      </Bloco>

      <Bloco rotulo="Documentação">
        {checklistEstado === 'erro' || (checklistEstado === 'ok' && !checklist) ? (
          <Tooltip title="Não foi possível carregar o checklist — veja a etapa Documentos">
            <Typography
              aria-label="Documentação indisponível"
              sx={{ fontWeight: 700, color: tokens.textSecondary }}
            >
              —
            </Typography>
          </Tooltip>
        ) : checklist && checklistEstado === 'ok' ? (
          <Box sx={{ minWidth: 180 }}>
            <Typography sx={{ fontWeight: 700, color: tokens.brandInk }}>
              {checklist.percentual}% · {checklist.presentes} de {checklist.total}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={Math.min(100, Math.max(0, checklist.percentual))}
              sx={{ mt: 0.75, height: 6, borderRadius: 3 }}
            />
          </Box>
        ) : (
          <Skeleton width={140} height={28} />
        )}
      </Bloco>
    </Paper>
  )
}

export default HeaderResumoEstruturacao
